(ns common.schema.entities.screening-profile.schema
  (:require [clojure.string :as string]
            [common.country :as country]
            [common.schema.entities.screening-profile.materialization :as materialization]
            [common.schema.entities.screening-profile.options :as options]
            [common.schema.schema-utils :as su]))

(defn- system-controlled
  [opts]
  (assoc opts :system-semantics su/system-controlled-optional-field-semantics))

(defn- system-controlled-not-clearable
  [opts]
  (assoc opts :system-semantics su/system-controlled-not-clearable-field-semantics))

(def schema
  (vec
   (concat
    (su/text-unique-global :screening-profile/uuid
                           "Screening profile UUID"
                           (system-controlled {:authorization su/access-system-user}))

    (su/text-simple-field :screening-profile/external-screening-id
                          "External screening ID"
                          (system-controlled {:authorization su/access-system-user}))

    (su/keyword-field :screening-profile/screening-status
                      "Screening status"
                      options/screening-statuses
                      (system-controlled-not-clearable {:authorization  su/access-system-user
                                                        :not-clearable? true}))

    (su/keyword-field :screening-profile/sanction-status
                      "Sanction status"
                      options/sanction-status-options
                      (system-controlled-not-clearable {:authorization  su/access-system-user
                                                        :not-clearable? true}))

    (su/boolean-field :screening-profile/pep-status
                      "PEP status"
                      (system-controlled {:authorization su/access-system-user}))

    (su/datetime-field :screening-profile/refresh-timestamp
                       "Refresh timestamp"
                       (system-controlled {:authorization su/access-system-user
                                           :no-history?   true}))

    (su/datetime-field :screening-profile/latest-pep-status-change
                       "Latest PEP status change"
                       (system-controlled {:authorization su/access-system-user}))

    (su/boolean-field :screening-profile/rca-status
                      "RCA status"
                      (system-controlled {:authorization su/access-system-user}))

    (su/datetime-field :screening-profile/latest-rca-status-change
                       "Latest RCA status change"
                       (system-controlled {:authorization su/access-system-user}))

      ; Person First name Last name or Company name
    (su/calculated-field :screening-profile/name
                         "Name"
                         {:data-type      :data.type/text
                          :default-field-visibility :in-use
                          :name-attribute :screening-profile/name
                          :calc-fn        (fn [{:keys [entity-data]}]
                                            (let [profile-type (:screening-profile/type entity-data)]
                                              (if (= profile-type :screening-profile-type/company)
                                                (:screening-profile/company-name entity-data)
                                                (str (:screening-profile/first-name entity-data)
                                                     " "
                                                     (:screening-profile/last-name entity-data)))))})

    (su/keyword-field :screening-profile/type
                      "Type"
                      options/screening-profile-types
                      (system-controlled {:authorization su/access-system-user}))

    (su/text-simple-field :screening-profile/company-name
                          "Company name"
                          (system-controlled {:authorization su/access-system-user}))

    (su/text-simple-field :screening-profile/first-name
                          "First name"
                          (system-controlled {:authorization su/access-system-user}))

    (su/text-simple-field :screening-profile/last-name
                          "Last name"
                          (system-controlled {:authorization su/access-system-user}))

    (su/date-field :screening-profile/date-of-birth
                   "Date of Birth"
                   (system-controlled {:authorization su/access-system-user}))

    (su/keyword-field :screening-profile/country
                      "Country"
                      country/options
                      (system-controlled {:authorization su/access-system-user}))

    (su/keyword-multi-field :screening-profile/additional-countries
                            "Additional countries"
                            country/options
                            {:authorization su/access-system-user})

    (su/text-simple-field :screening-profile/business-id
                          "Business ID"
                          (system-controlled {:authorization su/access-system-user}))

    (su/multi-ref-field :screening-profile/kyc-profiles
                        "KYC profiles"
                        [:entity.type/kyc-profile
                         :entity.type/tenant-kyc-profile
                         :entity.type/contract-kyc-profile]
                        (system-controlled {:authorization su/access-system-user
                                            :default-field-visibility :in-use}))

    (su/calculated-field :screening-profile/kyc-profile-labels "KYC profiles"
                         {:data-type            :data.type/text
                          :calculation-triggers {:entity.type/kyc-profile          [[:screening-profile/_kyc-profiles]]
                                                 :entity.type/tenant-kyc-profile   [[:screening-profile/_kyc-profiles]]
                                                 :entity.type/contract-kyc-profile [[:screening-profile/_kyc-profiles]]
                                                 :entity.type/investor             [[:kyc-profile/_target :screening-profile/_kyc-profiles]]
                                                 :entity.type/company              [[:tenant-kyc-profile/_target :screening-profile/_kyc-profiles]
                                                                                    [:contract-kyc-profile/_target :screening-profile/_kyc-profiles]]}
                          :calc-fn              (fn [{:keys [entity-data entity-lookup]}]
                                                  (let [kyc-profiles (->> entity-data
                                                                          :screening-profile/kyc-profiles
                                                                          (keep entity-lookup)
                                                                          (remove :common/disabled?))
                                                        target-names (->> kyc-profiles
                                                                          (map (fn [kyc-profile]
                                                                                 (let [entity-type (:entity/type kyc-profile)
                                                                                       target-id (case entity-type
                                                                                                   :entity.type/tenant-kyc-profile (:tenant-kyc-profile/target kyc-profile)
                                                                                                   :entity.type/contract-kyc-profile (:contract-kyc-profile/target kyc-profile)
                                                                                                   (:kyc-profile/target kyc-profile))
                                                                                       target (entity-lookup target-id)]
                                                                                   (when (and target (not (:common/disabled? target)))
                                                                                     (or (:investor/name target)
                                                                                         (:asset/name target))))))
                                                                          (remove nil?))]
                                                    (string/join "; " target-names)))})

    (su/multi-ref-field :screening-profile/counterparty-diligences
                        "Counterparty diligences"
                        [:entity.type/project-counterparty-diligence
                         :entity.type/person-counterparty-diligence
                         :entity.type/company-counterparty-diligence]
                        (system-controlled {:authorization su/access-system-user
                                            :default-field-visibility :in-use}))

    (su/calculated-field :screening-profile/counterparty-diligence-labels
                         "Counterparty diligences"
                         {:data-type :data.type/text
                          :calc-fn   (fn [{:keys [entity-data entity-lookup]}]
                                       (let [counterparty-diligences (->> entity-data
                                                                          :screening-profile/counterparty-diligences
                                                                          (keep entity-lookup)
                                                                          (remove :common/disabled?))
                                             entity-labels
                                             (->> counterparty-diligences
                                                  (mapv (fn [entity]
                                                          (case (:entity/type entity)
                                                            :entity.type/project-counterparty-diligence
                                                            (:project-counterparty-diligence/project-name entity)

                                                            :entity.type/person-counterparty-diligence
                                                            (:person-counterparty-diligence/person-name entity)

                                                            :entity.type/company-counterparty-diligence
                                                            (:company-counterparty-diligence/company-name entity)))))]
                                         (string/join "; " entity-labels)))})

    (su/calculated-field :screening-profile/pep-or-rca-mismatch
                         "PEP/RCA mismatch"
                         {:default-field-visibility :in-use
                          :data-type                :data.type/boolean
                          :composite-attrs          [:screening-profile/kyc-profiles :screening-profile/counterparty-diligences]
                          :calc-fn                  (fn [{:keys [entity-data entity-lookup display-string]}]
                                                      (if-not (= (:screening-profile/type entity-data)
                                                                 :screening-profile-type/person)
                                                        false
                                                        (let [kyc-profiles-and-pep-statuses (materialization/screening-profile->kyc-profiles-and-pep-statuses
                                                                                             {:screening-profile entity-data
                                                                                              :entity-lookup     entity-lookup
                                                                                              :display-string    display-string})

                                                              counterparty-diligences-and-pep-statuses (materialization/screening-profile->counterparty-diligences-and-pep-statuses
                                                                                                        {:screening-profile entity-data
                                                                                                         :entity-lookup     entity-lookup
                                                                                                         :display-string    display-string})

                                                              pep-or-rca-mismatch-data (materialization/calculate-pep-or-rca-mismatch-data
                                                                                        {:screening-profile                        entity-data
                                                                                         :kyc-profiles-and-pep-statuses            kyc-profiles-and-pep-statuses
                                                                                         :counterparty-diligences-and-pep-statuses counterparty-diligences-and-pep-statuses})

                                                              mismatch? (-> pep-or-rca-mismatch-data
                                                                            :mismatch?
                                                                            boolean)]
                                                          mismatch?)))})

    (su/multi-ref-field :screening-profile/person-profile
                        "Person profile"
                        [:entity.type/person]
                        {:authorization su/access-user-user
                         :default-field-visibility :in-use})

    (su/keyword-field :screening-profile/screening-service
                      "Screening service"
                      options/screening-service-options
                      (system-controlled {:authorization su/access-system-user})))))
